import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const AnalyticsWinLoss = ({ allGames }) => {
  const { monthly, runDiff, streaks } = useMemo(() => {
    if (!allGames?.length) return { monthly: [], runDiff: [], streaks: [] };

    const monthMap = {};
    const runDiffArr = [];
    let currentStreak = { type: null, count: 0 };
    const streakArr = [];

    const finished = allGames.filter(g => g.status?.abstractGameState === 'Final');

    finished.forEach((g, i) => {
      const bosHome = g.teams?.home?.team?.id === 111;
      const bosScore = bosHome ? g.teams.home.score : g.teams.away.score;
      const oppScore = bosHome ? g.teams.away.score : g.teams.home.score;
      const won = Number(bosScore) > Number(oppScore);
      const rd = Number(bosScore) - Number(oppScore);
      const date = new Date(g.gameDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short' });

      if (!monthMap[monthKey]) monthMap[monthKey] = { label: monthLabel, wins: 0, losses: 0 };
      if (won) monthMap[monthKey].wins++;
      else monthMap[monthKey].losses++;

      runDiffArr.push({ game: i + 1, rd, cumRd: runDiffArr.length ? runDiffArr[runDiffArr.length - 1].cumRd + rd : rd, won });

      if (currentStreak.type === null) {
        currentStreak = { type: won ? 'W' : 'L', count: 1 };
      } else if ((won && currentStreak.type === 'W') || (!won && currentStreak.type === 'L')) {
        currentStreak.count++;
      } else {
        streakArr.push({ ...currentStreak });
        currentStreak = { type: won ? 'W' : 'L', count: 1 };
      }
    });

    if (currentStreak.type) streakArr.push(currentStreak);

    return {
      monthly: Object.values(monthMap),
      runDiff: runDiffArr,
      streaks: streakArr.slice(-10),
    };
  }, [allGames]);

  const monthlyOption = {
    backgroundColor: 'transparent',
    grid: { top: 20, right: 20, bottom: 40, left: 44 },
    legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
    tooltip: { trigger: 'axis', backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
    xAxis: { type: 'category', data: monthly.map(m => m.label), axisLabel: { color: '#a9c9b1', fontSize: 9 }, axisLine: { lineStyle: { color: '#658d71' } } },
    yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#a9c9b1', fontSize: 9 }, axisLine: { lineStyle: { color: '#658d71' } }, splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } } },
    series: [
      { name: 'Wins', type: 'bar', data: monthly.map(m => m.wins), itemStyle: { color: 'rgba(169,201,177,0.85)', borderColor: '#a9c9b1', borderWidth: 1 }, stack: 'wl' },
      { name: 'Losses', type: 'bar', data: monthly.map(m => m.losses), itemStyle: { color: 'rgba(196,51,62,0.75)', borderColor: '#c4333e', borderWidth: 1 }, stack: 'wl' },
    ],
  };

  const runDiffOption = {
    backgroundColor: 'transparent',
    grid: { top: 20, right: 20, bottom: 30, left: 50 },
    tooltip: { trigger: 'axis', backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
    xAxis: { type: 'value', axisLabel: { show: false }, axisLine: { lineStyle: { color: '#658d71' } }, splitLine: { show: false } },
    yAxis: { type: 'value', axisLabel: { color: '#a9c9b1', fontSize: 9 }, axisLine: { lineStyle: { color: '#658d71' } }, splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } } },
    series: [{
      type: 'line',
      data: runDiff.map(d => [d.game, d.cumRd]),
      smooth: true,
      lineStyle: { color: '#d8ceb3', width: 2 },
      areaStyle: {
        color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(216,206,179,0.3)' }, { offset: 1, color: 'rgba(216,206,179,0.02)' }] }
      },
      symbol: 'none',
    }],
  };

  if (!monthly.length) return (
    <div className="text-[#658d71] text-xs text-center py-8 uppercase tracking-widest">No game data available</div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Monthly Win/Loss Record</span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Stacked wins vs losses by month</p>
        </div>
        <div className="p-2"><ReactECharts option={monthlyOption} style={{ height: '180px' }} /></div>
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Cumulative Run Differential</span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Season-long scoring edge trend</p>
        </div>
        <div className="p-2"><ReactECharts option={runDiffOption} style={{ height: '160px' }} /></div>
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Recent Streaks</span>
        </div>
        <div className="p-3 flex flex-wrap gap-2">
          {streaks.map((s, i) => (
            <div key={i} className={`flex flex-col items-center border px-3 py-2 min-w-[52px] ${s.type === 'W' ? 'border-[#a9c9b1] bg-[rgba(169,201,177,0.08)]' : 'border-[rgba(196,51,62,0.5)] bg-[rgba(196,51,62,0.05)]'}`}>
              <span className={`text-xl font-black tabular-nums ${s.type === 'W' ? 'text-[#a9c9b1]' : 'text-[#c4333e]'}`}>{s.count}</span>
              <span className={`text-[9px] font-black uppercase tracking-widest ${s.type === 'W' ? 'text-[#a9c9b1]' : 'text-[#c4333e]'}`}>{s.type}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsWinLoss;